/**
 * Why a request failed, in words short enough for a dashboard banner.
 *
 * Exists because the dashboards used to swallow their errors — `catch {}` and a fixed
 * "couldn't read X" — which is indistinguishable across the failures that matter and have
 * completely different fixes:
 *
 *   503  the site is offline; nothing to fix here, wait for the tunnel
 *   504  the node took longer than the tunnel's 30s; a slow query or a loaded machine
 *   403  a permission or, on the command-center, the service token / IP allow-list
 *   500  the node itself errored, and its message is the only thing that says why
 *
 * An operator told "couldn't read AI summary" cannot tell those apart, and neither can whoever
 * they escalate to. The status and the server's own message are already in the response; this
 * just stops them being thrown away.
 */
export function describeApiError(err: any): string {
  // Axios shape. `data` is the cloud backend's `{ success, message }` envelope, or the node's.
  const status = err?.response?.status;
  const data = err?.response?.data;
  const fromBody =
    typeof data === 'string'
      ? data
      : data?.message || data?.error || data?.data?.message || undefined;

  const detail = String(fromBody || err?.message || 'unknown error')
    .replace(/^Error:\s*/, '')
    // Banner text, so keep it to one line however the server formatted it.
    .replace(/\s+/g, ' ')
    .trim();

  const capped = detail.length > 140 ? `${detail.slice(0, 139)}…` : detail;
  return status ? `${status} ${capped}` : capped;
}
